import { userServiceApiSlice } from "state/services/userServiceApiSlice";
import { METERED_USAGE_CACHE_SECONDS } from "./meteredUsageCache";
import { adRequestStatusMap } from "utils/constants";
import { generateRandomColor } from "utils/generateRandomColor";
import { moveOrgArrayElement } from "utils/moveOrgArrayElement";

// Builds a metered-usage URL. Every call needs platform_id and an inclusive from/to range; the org tier also needs
// org_id, the enterprise tier (scope "enterprise") never sends it. Optional filters are left off when empty.
const meteredUsageUrl = (resource, args, extras = []) => {
  const { scope, platformId, orgId, fromDate, toDate, userId, costCenterId, product, sku } = args;
  const params = new URLSearchParams({ platform_id: platformId, from_date: fromDate, to_date: toDate });
  if (scope !== "enterprise" && orgId) params.set("org_id", orgId);
  if (userId) params.set("user_id", userId);
  if (costCenterId) params.set("cost_center_id", costCenterId);
  if (product) {
    params.set("product", product);
    if (sku) params.set("sku", sku);
  }
  const names = { compare: "compare", groupBy: "group_by", metric: "metric", limit: "limit", granularity: "granularity", sortBy: "sort_by", sortOrder: "sort_order", page: "page", perPage: "per_page", search: "search" };
  extras.forEach((key) => {
    if (args[key] !== undefined && args[key] !== null && args[key] !== "") params.set(names[key], String(args[key]));
  });
  const prefix = scope === "enterprise" ? "/users/metered-usage/enterprise" : "/users/metered-usage";
  return `${prefix}/${resource}?${params.toString()}`;
};

// The `userServiceApiQueries` object is used to generate hooks that can be used to make API calls to the user service.
export const userServiceApiQueries = userServiceApiSlice.injectEndpoints({
  // The `endpoints` object is used to define the API endpoints for the user service.
  endpoints: (builder) => ({
    // The `getUser` query is used to retrieve a user entry by email.
    getUser: builder.query({
      // Accepts email and optional platformId
      query: ({ email, platformId }) => {
        let url = `/users?email=${encodeURIComponent(email)}`;
        if (platformId) {
          url += `&platform_id=${encodeURIComponent(platformId)}`;
        }
        return { url };
      },
      transformResponse: (responseData) => {
        return responseData.users;
      },
      providesTags: [{ type: "User", id: "Entry" }],
    }),
    // The `getUserByEmail` query fetches a user by email only (no platform filter), returning data across all platforms.
    getUserByEmail: builder.query({
      query: ({ email }) => ({
        url: `/users?email=${encodeURIComponent(email)}`,
      }),
      transformResponse: (responseData) => {
        return responseData.users;
      },
      providesTags: [{ type: "User", id: "EntryByEmail" }],
    }),
    // The `getUserWithOrgMembershipStatuses` query is used to retrieve a user with their organization membership statuses.
    getUserWithOrgMembershipStatuses: builder.query({
      query: ({ userId, platformId }) => ({
        url: `/users/${userId}/organization/membership?platform_id=${platformId}`,
      }),
      transformResponse: (responseData, meta, { userId, platformId }) => {
        // The `orgsListFormatted` array is used to store the formatted organization membership statuses for the user.
        try {
          const email = responseData?.email;
          const orgsListWithUserStatus =
            responseData?.platforms?.[platformId]?.organizations;

          // Split orgs into two arrays: joined/recommended and non-recommended
          let orgs = [{}, {}];

          // Add orgs with "Joined" status to orgs object
          for (const orgId in orgsListWithUserStatus) {
            if (orgsListWithUserStatus[orgId].membership_status === "Joined") {
              orgs[0][orgId] = orgsListWithUserStatus[orgId];
            }
          }

          // Add recommended orgs to orgs object
          for (const orgId in orgsListWithUserStatus) {
            if (
              orgsListWithUserStatus[orgId].recommended === true &&
              orgsListWithUserStatus[orgId].membership_status !== "Joined"
            ) {
              orgs[0][orgId] = orgsListWithUserStatus[orgId];
            }
          }

          // Add non-recommended orgs to orgs object
          for (const orgId in orgsListWithUserStatus) {
            if (
              orgsListWithUserStatus[orgId].recommended === false &&
              orgsListWithUserStatus[orgId].membership_status !== "Joined"
            ) {
              orgs[1][orgId] = orgsListWithUserStatus[orgId];
            }
          }

          // Format orgs object into orgsListFormatted array, maintaining split between joined/recommended and non-recommended orgs
          const orgsListFormatted = [[], []];
          let i = 1;

          for (const orgId in orgs[0]) {
            if (
              orgsListWithUserStatus[orgId].visible === true || // Modification
              orgsListWithUserStatus[orgId].user_type === "Owner" || // Modification
              orgsListWithUserStatus[orgId].user_type === "Approver" // Modification
            ) {
              orgsListFormatted[0].push({
                s_no: i++,
                id: orgId,
                orgDisplayName: orgsListWithUserStatus[orgId].org_name.replace(
                  /-/g,
                  " "
                ),
                orgName: orgsListWithUserStatus[orgId].org_name,
                status:
                  adRequestStatusMap[
                  orgsListWithUserStatus[orgId].membership_status
                  ],
                action:
                  adRequestStatusMap[
                    orgsListWithUserStatus[orgId].membership_status
                  ] === "Non-Member"
                    ? "Add"
                    : adRequestStatusMap[
                      orgsListWithUserStatus[orgId].membership_status
                    ] === "Failed"
                      ? "Add"
                      : "Delete",
                email: email,
                approvalRequired:
                  orgsListWithUserStatus[orgId]?.approval_required === true
                    ? "Yes"
                    : "No",
                userType: orgsListWithUserStatus[orgId]?.user_type,
                //userType: "Owner",
                signup_banner: orgsListWithUserStatus[orgId]?.signup_banner
                  ? orgsListWithUserStatus[orgId].signup_banner
                  : "No description available",
                membershipADGroup:
                  orgsListWithUserStatus[orgId]?.membership_ad_group,
                userId: userId,
                platformId: platformId,
                recommended: orgsListWithUserStatus[orgId]?.recommended,
                user_removal_enabled:
                  orgsListWithUserStatus[orgId]?.user_removal_enabled,
              });
            }
          }

          for (const orgId in orgs[1]) {
            if (
              orgsListWithUserStatus[orgId].visible === true || // Modification
              orgsListWithUserStatus[orgId].user_type === "Owner" || // Modification
              orgsListWithUserStatus[orgId].user_type === "Approver" // Modification
            ) {
              orgsListFormatted[1].push({
                s_no: i++,
                id: orgId,
                orgDisplayName: orgsListWithUserStatus[orgId].org_name.replace(
                  /-/g,
                  " "
                ),
                orgName: orgsListWithUserStatus[orgId].org_name,
                status:
                  adRequestStatusMap[
                  orgsListWithUserStatus[orgId].membership_status
                  ],
                action:
                  adRequestStatusMap[
                    orgsListWithUserStatus[orgId].membership_status
                  ] === "Non-Member"
                    ? "Add"
                    : adRequestStatusMap[
                      orgsListWithUserStatus[orgId].membership_status
                    ] === "Failed"
                      ? "Add"
                      : "Delete",
                email: email,
                approvalRequired:
                  orgsListWithUserStatus[orgId]?.approval_required === true
                    ? "Yes"
                    : "No",
                userType: orgsListWithUserStatus[orgId]?.user_type,
                //userType: "Owner",
                signup_banner: orgsListWithUserStatus[orgId]?.signup_banner
                  ? orgsListWithUserStatus[orgId].signup_banner
                  : "No description available",
                membershipADGroup:
                  orgsListWithUserStatus[orgId]?.membership_ad_group,
                userId: userId,
                platformId: platformId,
                recommended: orgsListWithUserStatus[orgId]?.recommended,
                user_removal_enabled:
                  orgsListWithUserStatus[orgId]?.user_removal_enabled,
              });
            }
          }

          return orgsListFormatted;
        } catch (error) {
          console.log(error);
          return error;
        }
      },
      providesTags: [{ type: "User", id: "OrgStatuses" }],
    }),
    // The `getUsersByOrgAndStatus` query is used to retrieve users by organization and status.
    getUsersByOrgAndStatus: builder.query({
      query: ({ orgId, status, platformId, page, perPage, email, sortBy, sortOrder, filterField, filterValue, extraFilters }) => {
        let url = `/users?org_id=${orgId}&membership_status=${status}`;

        if (page && perPage) {
          url += `&page=${page}&per_page=${perPage}`;
        }

        if (email) {
          url += `&email=${email}`;
        }

        if (sortBy) {
          url += `&sort_by=${sortBy}`;
        }

        if (sortOrder) {
          url += `&sort_order=${sortOrder}`;
        }

        if (filterField && filterValue) {
          url += `&${filterField}=${encodeURIComponent(filterValue)}`;
        }

        // Several filters at once ({ param: value }); the API ANDs them together.
        Object.entries(extraFilters ?? {}).forEach(([param, value]) => {
          if (value !== undefined && value !== null && value !== "") url += `&${param}=${encodeURIComponent(value)}`;
        });
        // has_copilot_license is only accepted together with org_id and platform_id (400 otherwise).
        if (extraFilters?.has_copilot_license && platformId) url += `&platform_id=${encodeURIComponent(platformId)}`;

        return { url };
      },
      transformResponse: (
        responseData,
        meta,
        { orgId, platformId, status }
      ) => {
        try {
          const formattedOrgRequests = {
            total: responseData?.total,
            users: [],
          };

          // Ensure responseData.users exists and is iterable
          if (!responseData?.users || !Array.isArray(responseData.users)) {
            return formattedOrgRequests;
          }

          for (const request of responseData.users) {
            const userRequestForOrg =
              request.platforms[platformId].organizations[orgId];
            const allUserOrgs =
              request?.platforms?.[platformId]?.organizations || {};
            const hasJoinedAnyOrg =
              typeof request?.is_new_user === "boolean"
                ? !request.is_new_user
                : Object.values(allUserOrgs).some(
                    (org) => org?.membership_status === "Joined"
                  );

            const date = new Date(
              userRequestForOrg?.status_history[
                userRequestForOrg?.status_history.length - 1
              ]?.changed_date
            );

            const options = {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            };

            const formattedDate = date.toLocaleDateString("en-US", options);

            let fullName = "";
            if (request?.last_name) {
              fullName += `${request?.last_name}, `;
            }
            if (request?.first_name) {
              fullName += `${request?.first_name}`;
            }

            formattedOrgRequests.users.push({
              id: request._id,
              name: fullName,
              firstName: request?.first_name,
              lastName: request?.last_name,
              reason: userRequestForOrg?.reason,
              date: formattedDate,
              costCenter: request.platforms[platformId]?.cost_center_id || "",
              action:
                status === "Pending"
                  ? "approve"
                  : status === "Joined"
                    ? "remove"
                    : status === "Rejected"
                      ? "reset"
                      : status === "Remove-Requested"
                        ? "remove-requested"
                        : status === "Removed"
                          ? "removed"
                          : "",
              status: status.toLowerCase(),
              email: request.email,
              // costCenter: "Cost Center 1",
              // changedBy:
              //   userRequestForOrg?.status_history[
              //     userRequestForOrg?.status_history?.length - 1
              //   ]?.changed_by,
              changedBy: (() => {
                const history = userRequestForOrg?.status_history || [];
                if (!history.length) return "";
                const normalizeStatus = (value) =>
                  String(value || "")
                    .trim()
                    .toLowerCase();

                // History entries store the status before transition; map each
                // table status to the source status that identifies the actor.
                const actorSourceStatusByStatus = {
                  requested: "pending",
                  joined: "pending",
                  rejected: "pending",
                  "remove-requested": "joined",
                  removed: "joined",
                };

                const actorSourceStatus =
                  actorSourceStatusByStatus[normalizeStatus(status)] ||
                  normalizeStatus(status);
                const matchingEntries = history.filter(
                  (entry) =>
                    normalizeStatus(entry.status) === actorSourceStatus
                );
                const entriesToSearch =
                  matchingEntries.length > 0 ? matchingEntries : history;

                const latestEntry = entriesToSearch.reduce((latest, current) =>
                  new Date(current.changed_date) > new Date(latest.changed_date)
                    ? current
                    : latest
                );

                return latestEntry?.changed_by ?? "";
              })(),
              color: generateRandomColor(request?._id),
              rawDate: userRequestForOrg?.status_history?.reduce((max, entry) => {
                const d = new Date(entry.changed_date);
                return d > new Date(max) ? entry.changed_date : max;
              }, userRequestForOrg?.status_history?.[0]?.changed_date),
              isNewUser: !hasJoinedAnyOrg,
              hasUserName: !!request.platforms[platformId]?.user_name,
              hasEndDate: !!request.platforms[platformId]?.end_date,
              // Present only when this user has a Copilot license — its
              // `licensed_organizations` array is what drives the Copilot
              // badge/tooltip in JoinedTab.
              copilotBudget: request?.copilot_budget,
            });
          }

          return formattedOrgRequests;
        } catch (err) {
          console.log(err);
          return err;
        }
      },
      providesTags: (result, error, { status }) => [
        { type: "User", id: `OrgRequests-${status}` }
      ],
    }),
    getCostCenterUsers: builder.query({
      query: () => ({
        url: `/users/costCenters`,
      }),
    }),
    getOrgUserSyncStatus: builder.query({
      query: ({ orgId }) => ({
        url: `/sync/tasks/verify-pending/org/${orgId}`,
      }),
      transformResponse: (responseData) => {
        const statusMap = {
          accepted: "in_progress",
          queued: "in_progress",
          starting: "in_progress",
          in_progress: "in_progress",
          already_running: "in_progress",
          ended: "complete",
          complete: "complete",
          never_run: "never_run",
          error: "error",
        };

        return {
          ...responseData,
          status: statusMap[responseData?.status] || responseData?.status,
        };
      },
      transformErrorResponse: (error) => {
        if (
          error.error_code === 404 &&
          error.error_message === "No sync found for the given organization"
        ) {
          return {
            error_code: 404,
            error_message: "No sync found for the given organization",
          };
        }

        return error;
      },
    }),
    getActiveUserStatus: builder.query({
      query: ({ email }) => ({
        url: `/users/usertracker?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    getAdCheckEmail: builder.query({
      query: (email) => ({
        url: `/users/ad/check-email?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    // The `getUserTrackers` query is used to retrieve user trackers with pagination, status, and country filter
    getUserTrackers: builder.query({
      query: ({ page, perPage, country, status, searchParams }) => {
        let url = `/users/usertrackers?page=${page}&per_page=${perPage}`;
        if (country) {
          url += `&country=${encodeURIComponent(country)}`;
        }
        if (status && status !== "all") {
          url += `&status=${encodeURIComponent(status)}`;
        }
        if (searchParams && searchParams.trim()) {
          url += `&searchParams=${encodeURIComponent(searchParams.trim())}`;
        }
        return { url };
      },
      transformResponse: (responseData) => {
        return {
          userTrackers: responseData.user_trackers || [],
          total: responseData.total || 0,
          page: responseData.page || 1,
          perPage: responseData.per_page || 20,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "UserTrackers", id: `${arg.country}-${arg.status}-${arg.page}-${arg.perPage}-${arg.searchParams || ''}` }
      ],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    // The `getUserGitHubMembership` query retrieves a user's GitHub org memberships by email and platform.
    getUserGitHubMembership: builder.query({
      query: ({ email, platformId }) => ({
        url: `/users/user-github-membership?platform_id=${encodeURIComponent(platformId)}&email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => {
        if (responseData?.organizations?.length > 0) {
          return responseData.organizations[0];
        }
        return null;
      },
      providesTags: [{ type: "User", id: "GitHubMembership" }],
    }),
    // The `getUserBudget` query retrieves a user's AI Credit budget summary:
    // every budget scope that could apply to them (enterprise/cost-center/user),
    // which one is currently effective, any open increase/decrease request,
    // and which orgs they hold a Copilot license in. Budgets/requests change
    // outside this client (admin actions, GitHub usage), so this always
    // refetches on mount/arg-change instead of serving a stale cached value.
    getUserBudget: builder.query({
      query: ({ email, platformId }) => ({
        url: `/users/copilot/budget/${encodeURIComponent(email)}?platform_id=${encodeURIComponent(platformId)}`,
        method: "GET",
      }),
      providesTags: [{ type: "User", id: "Budget" }],
      forceRefetch: () => true,
    }),
    // The `getBudgetRequests` query retrieves a paginated, filterable list of
    // AI Credit budget requests for an org — used by the admin "AIC Budget
    // Requests" table. Defaults mirror the backend's own defaults so the
    // pending-approvals view loads with `status=pending` out of the box.
    // Always refetches — other admins may review requests concurrently.
    getBudgetRequests: builder.query({
      query: ({
        orgId,
        platformId,
        action = "all",
        status = "pending",
        page = 1,
        perPage = 20,
        email = "",
        requestDate = "",
        requestDateTo = "",
        costCenterId = "",
        reviewedBy = "",
        sortBy = "",
        sortOrder = "asc",
      }) => {
        let url = `/users/copilot/budget-requests?org_id=${encodeURIComponent(orgId)}&platform_id=${encodeURIComponent(platformId)}&action=${encodeURIComponent(action)}&status=${encodeURIComponent(status)}&page=${page}&per_page=${perPage}`;
        if (email) url += `&email=${encodeURIComponent(email)}`;
        // request_date alone is a single day; with request_date_to it is the inclusive range.
        if (requestDate) url += `&request_date=${encodeURIComponent(requestDate)}`;
        if (requestDate && requestDateTo) url += `&request_date_to=${encodeURIComponent(requestDateTo)}`;
        // Comma-separated ids for several cost centers.
        if (costCenterId) url += `&cost_center_id=${encodeURIComponent(costCenterId)}`;
        // Case-insensitive substring of the reviewer's email; the API only accepts it with an
        // approved / rejected / failed status (400 otherwise), so callers send it only then.
        if (reviewedBy) url += `&reviewed_by=${encodeURIComponent(reviewedBy)}`;
        if (sortBy) url += `&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}`;
        return { url, method: "GET" };
      },
      providesTags: (result, error, { action = "all", status = "pending" }) => [
        { type: "User", id: `BudgetRequests-${action}-${status}` },
      ],
      forceRefetch: () => true,
    }),
    // The `getMyBudgetRequests` query retrieves the signed-in user's own AI
    // Credit budget request history (every org, every status) — used by the
    // "My Requests" tab in UlbRequestModal. Always refetches — an admin may
    // review a request while this tab is open.
    // ECR Ops "Cost Centers": the cost centers on a platform. `state` is "active" or "deleted"; passing
    // `costCenterId` fetches one by its own id (an invalid id is a 400). The raw `resources` list (thousands
    // of GitHub logins) is left out unless `includeResources` is set — use getCostCenterMembers for users.
    // GET /api/users/costCenters?platform_id=&state=&cost_center_id=&include_resources=
    getCostCenterList: builder.query({
      query: ({ platformId, state, costCenterId, includeResources = false } = {}) => {
        const params = new URLSearchParams();
        if (platformId) params.set("platform_id", platformId);
        if (state) params.set("state", state);
        if (costCenterId) params.set("cost_center_id", costCenterId);
        if (includeResources) params.set("include_resources", "true");
        const qs = params.toString();
        return { url: `/users/costCenters${qs ? `?${qs}` : ""}` };
      },
      transformResponse: (responseData) =>
        Array.isArray(responseData?.cost_centers) ? responseData.cost_centers : Array.isArray(responseData) ? responseData : [],
    }),
    // The users in one cost center (or several, comma-separated, or the whole platform when none is given),
    // each with their Copilot budget (null when they have no budget record at all).
    // GET /api/users/copilot/cost-centers/users?platform_id=&cost_center_id=&page=&per_page=&search=&has_copilot_license=&budget_scope=&sort_by=&sort_order=
    getCostCenterMembers: builder.query({
      query: ({ platformId, costCenterId, page = 1, perPage = 20, search, hasCopilotLicense, budgetScope, sortBy, sortOrder }) => {
        const params = new URLSearchParams({ platform_id: platformId, page: String(page), per_page: String(perPage) });
        if (costCenterId) params.set("cost_center_id", Array.isArray(costCenterId) ? costCenterId.join(",") : costCenterId);
        if (search) params.set("search", search);
        if (hasCopilotLicense === true || hasCopilotLicense === "true") params.set("has_copilot_license", "true");
        if (hasCopilotLicense === false || hasCopilotLicense === "false") params.set("has_copilot_license", "false");
        if (budgetScope) params.set("budget_scope", budgetScope);
        if (sortBy) params.set("sort_by", sortBy);
        if (sortOrder) params.set("sort_order", sortOrder);
        return { url: `/users/copilot/cost-centers/users?${params.toString()}` };
      },
    }),
    // ── Metered usage (billing usage across every GitHub product) ──────────────────────────────────
    // GET /api/users/metered-usage/{summary|trend|products|users|users/{id}|cost-centers}            (org tier, needs org_id)
    // GET /api/users/metered-usage/enterprise/{same, plus organizations}                            (every org, no org_id)
    // The summary and the people list are the slow reports: they are tagged "MeteredUsageHeavy" (the rest "MeteredUsageLight")
    // so the timed refresh can reload them less often (see useMeteredUsageRefresh).
    // `scope` picks the tier. product/sku are sent as-is from the /products response (they aren't a fixed list);
    // sku only goes along when product does.
    getMeteredUsageSummary: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageHeavy"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("summary", args, ["compare"]) }),
    }),
    getMeteredUsageTrend: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("trend", args, ["granularity", "groupBy"]) }),
    }),
    getMeteredUsageProducts: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("products", { ...args, product: undefined, sku: undefined }) }),
    }),
    getMeteredUsageUsers: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageHeavy"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("users", args, ["sortBy", "sortOrder", "page", "perPage", "search"]) }),
    }),
    getMeteredUsageUserDetail: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({
        url: meteredUsageUrl(`users/${encodeURIComponent(args.userId)}`, { ...args, userId: undefined, costCenterId: undefined, product: undefined, sku: undefined }),
      }),
    }),
    getMeteredUsageCostCenters: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("cost-centers", { ...args, userId: undefined, costCenterId: undefined }, ["sortBy", "sortOrder"]) }),
    }),
    getMeteredUsageOrganizations: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({
        url: meteredUsageUrl("organizations", { ...args, scope: "enterprise", orgId: undefined, userId: undefined, costCenterId: undefined }, ["sortBy", "sortOrder"]),
      }),
    }),
    // Queues a grouped metered-usage CSV for the current scope and filters. Returns { job_id, status: "queued" }; the
    // job is then polled and downloaded through the shared /users/export/jobs endpoints. Org tier needs org_id and is
    // capped at 90 days, the enterprise tier at 31. 503 means export isn't enabled / configured on the server.
    // POST /api/users/metered-usage/[enterprise/]export/jobs?cost_center_id=&user_id=&product=&sku=
    startMeteredUsageExportJob: builder.mutation({
      query: (args) => ({ url: meteredUsageUrl("export/jobs", args), method: "POST" }),
    }),
    // Biggest increases and decreases against the previous equal-length window. group_by is user | product |
    // cost_center (organization too on the enterprise tier). Takes no user filter. limit is per direction (max 50).
    // GET /api/users/metered-usage/[enterprise/]movers?group_by=&metric=&limit=&cost_center_id=&product=&sku=
    getMeteredUsageMovers: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("movers", { ...args, userId: undefined }, ["groupBy", "metric", "limit"]) }),
    }),
    // How far the billing records can be trusted: attribution gaps and which dates have records (max 366 days).
    // GET /api/users/metered-usage/[enterprise/]quality?user_id=&cost_center_id=&product=&sku=
    getMeteredUsageQuality: builder.query({
      providesTags: ["MeteredUsage", "MeteredUsageLight"],
      keepUnusedDataFor: METERED_USAGE_CACHE_SECONDS,
      query: (args) => ({ url: meteredUsageUrl("quality", args) }),
    }),
    getMyBudgetRequests: builder.query({
      query: ({ email, platformId, status = "all", page = 1, perPage = 20 }) => ({
        url: `/users/copilot/budget/${encodeURIComponent(email)}/requests?platform_id=${encodeURIComponent(platformId)}&status=${encodeURIComponent(status)}&page=${page}&per_page=${perPage}`,
        method: "GET",
      }),
      providesTags: (result, error, { status = "all" }) => [
        { type: "User", id: `MyBudgetRequests-${status}` },
      ],
      forceRefetch: () => true,
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetUserByEmailQuery,
  useGetUserWithOrgMembershipStatusesQuery,
  useLazyGetUserWithOrgMembershipStatusesQuery,
  useGetUsersByOrgAndStatusQuery,
  useLazyGetUsersByOrgAndStatusQuery,
  useGetCostCenterUsersQuery,
  useGetOrgUserSyncStatusQuery,
  useLazyGetActiveUserStatusQuery,
  useGetActiveUserStatusQuery,
  useGetAdCheckEmailQuery,
  useLazyGetAdCheckEmailQuery,
  useGetUserTrackersQuery,
  useLazyGetUserTrackersQuery,
  useGetUserGitHubMembershipQuery,
  useLazyGetUserGitHubMembershipQuery,
  useGetUserBudgetQuery,
  useLazyGetUserBudgetQuery,
  useGetBudgetRequestsQuery,
  useGetMyBudgetRequestsQuery,
  useGetCostCenterListQuery,
  useGetCostCenterMembersQuery,
  useGetMeteredUsageSummaryQuery,
  useGetMeteredUsageTrendQuery,
  useGetMeteredUsageProductsQuery,
  useGetMeteredUsageUsersQuery,
  useGetMeteredUsageUserDetailQuery,
  useGetMeteredUsageCostCentersQuery,
  useGetMeteredUsageOrganizationsQuery,
  useGetMeteredUsageMoversQuery,
  useGetMeteredUsageQualityQuery,
  useStartMeteredUsageExportJobMutation,
} = userServiceApiQueries;
